import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useAuth, useCan } from "@/context/AuthContext";
import { PrinterCard } from "@/components/PrinterCard";
import { useOnline } from "@/lib/connectivity";
import { NeedsInternetNote } from "@/components/Offline";
import { InventoryCard } from "@/components/InventoryCard";
import { ReturnSettingsCard } from "@/components/ReturnSettingsCard";
import { WhatsAppSettingsCard } from "@/components/WhatsAppSettingsCard";
import { CountryPicker, RegionSelect, taxOf, useCountries } from "@/components/CountryPicker";

const LABELS = ["VAT", "GST", "HST", "GST+PST", "GST+QST", "Sales Tax", "Consumption Tax", "None", "Tax"];
const RATE_ERR = "Enter a rate between 0 and 100";
const rateOk = (v) => /^\d{1,3}(\.\d{1,3})?$/.test(String(v).trim()) && Number(v) <= 100;

function Row({ label, hint, children }) {
  return (
    <div className="grid gap-2 border-b border-slate-100 py-5 last:border-0 sm:grid-cols-[220px_1fr] sm:gap-6">
      <div>
        <div className="font-semibold text-slate-900">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function Settings() {
  const { session, setBusiness } = useAuth();
  const biz = session.business;
  const countries = useCountries();
  const [f, setF] = useState({
    country: biz.country, region: biz.region, tax_label: biz.tax_label, tax_rate: biz.tax_rate, tax_number: biz.tax_number, receipt_header: biz.receipt_header,
    receipt_footer: biz.receipt_footer, receipt_width: biz.receipt_width, tax_inclusive: biz.tax_inclusive,
  });
  const [busy, setBusy] = useState(false);
  const can = useCan();
  const online = useOnline();

  const c = countries.find((x) => x.code === f.country);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const pickTax = (country, region) => {
    const t = taxOf(countries.find((x) => x.code === country), region);
    setF((s) => ({ ...s, country, region: t?.region || null, tax_label: t?.tax_label ?? s.tax_label, tax_rate: t?.tax_rate ?? s.tax_rate }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!rateOk(f.tax_rate)) return toast.error(RATE_ERR);
    setBusy(true);
    try {
      const { data } = await api.put("/settings", f);
      setBusiness(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!can("settings")) {
    return (
      <div className="h-full overflow-y-auto" data-testid="settings-page">
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-8">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Settings</h1>
          <div className="mt-8"><PrinterCard /></div>
          <p className="mt-4 text-sm text-slate-500" data-testid="settings-no-permission">Business settings are managed by the owner.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="settings-page">
      <form onSubmit={save} className="mx-auto max-w-3xl px-4 py-8 sm:px-8">
        <div className="eyebrow">{biz.business_type_label}</div>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Settings</h1>
        <div className="mt-8"><PrinterCard /></div>
        {biz.inventory_entitled && session.user.role === "owner" && <InventoryCard />}
        {session.user.role === "owner" && <ReturnSettingsCard />}
        {session.user.role === "owner" && <WhatsAppSettingsCard />}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white px-5 sm:px-6">
          <Row label="Country" hint="Drives currency, decimals and default tax.">
            <div className="space-y-3">
              <CountryPicker value={f.country} countries={countries} onChange={(v) => pickTax(v, null)} testid="settings-country" />
              <RegionSelect country={c} value={f.region} onChange={(v) => pickTax(f.country, v)} testid="settings-region" />
            </div>
            {c && (
              <div className="mt-2 font-mono text-xs text-slate-500" data-testid="settings-country-derived">
                {c.currency} · {c.decimals} decimals · standard {taxOf(c, f.region).tax_label} {taxOf(c, f.region).tax_rate}%
              </div>
            )}
          </Row>
          <Row label="Tax" hint="Preselected from your country; edit if your business uses a different rate.">
            <div className="flex flex-wrap gap-2">
              <select value={f.tax_label} onChange={set("tax_label")} data-testid="settings-tax-label-select" className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold">
                {LABELS.map((l) => <option key={l}>{l}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <Input className="h-11 w-28 font-mono" value={f.tax_rate} onChange={set("tax_rate")} inputMode="decimal" data-testid="settings-tax-rate-input" />
                <span className="text-sm font-semibold text-slate-500">%</span>
              </div>
            </div>
            {!rateOk(f.tax_rate) && <div className="mt-1 text-xs font-semibold text-rose-600" data-testid="settings-tax-rate-error">{RATE_ERR}</div>}
            <p className="mt-2 text-xs text-slate-500" data-testid="settings-tax-note">Rates are standard rates — please confirm with your tax advisor</p>
          </Row>
          <Row label="Tax number" hint={`Printed on receipts as ${f.tax_label} No.`}>
            <Input className="h-11 font-mono" value={f.tax_number} onChange={set("tax_number")} placeholder="e.g. NTN / TRN / VATIN / GSTIN" data-testid="settings-tax-number-input" />
          </Row>
          <Row label="Prices include tax" hint="Off = tax is added on top of product prices.">
            <div className="flex h-11 items-center gap-3">
              <Switch checked={f.tax_inclusive} onCheckedChange={(v) => setF((s) => ({ ...s, tax_inclusive: v }))} data-testid="settings-tax-inclusive-switch" />
              <span className="text-sm text-slate-600" data-testid="settings-tax-mode-label">{f.tax_inclusive ? "Tax-inclusive" : "Tax-exclusive"}</span>
            </div>
          </Row>
          <Row label="Receipt width" hint="Match your thermal paper roll.">
            <div className="grid max-w-xs grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
              {["58mm", "80mm"].map((w) => (
                <button type="button" key={w} onClick={() => setF((s) => ({ ...s, receipt_width: w }))} data-testid={`settings-width-${w}`} className={`h-10 rounded-lg font-mono text-sm font-semibold ${f.receipt_width === w ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                  {w}
                </button>
              ))}
            </div>
          </Row>
          <Row label="Receipt header" hint="Address, phone — one per line.">
            <Textarea rows={3} value={f.receipt_header} onChange={set("receipt_header")} data-testid="settings-receipt-header-input" />
          </Row>
          <Row label="Receipt footer">
            <Textarea rows={2} value={f.receipt_footer} onChange={set("receipt_footer")} data-testid="settings-receipt-footer-input" />
          </Row>
        </div>
        {!online && <NeedsInternetNote className="mt-4" />}
        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={busy || !online} data-testid="settings-save-button" className="flex h-12 items-center gap-2 rounded-xl bg-slate-900 px-6 font-semibold text-white hover:bg-slate-800 disabled:bg-slate-300">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save settings
          </button>
        </div>
      </form>
    </div>
  );
}
